import type { PackagePricingMode } from '../../types/packages';
import PackagePhoneSheet, { type PackagePhoneItem } from './PackagePhoneSheet';
import { PHONE_COPY, phoneAed, phoneShortDate } from './packagePreviewCopy';

export type { PackagePhoneItem } from './PackagePhoneSheet';

export interface PackagePhonePreviewProps {
  lang: 'en' | 'ar';
  theme: 'light' | 'dark';
  garageName: string;
  nameEn: string;
  nameAr?: string | null;
  descriptionEn?: string | null;
  descriptionAr?: string | null;
  coverUrl?: string | null;
  mode: PackagePricingMode;
  originalTotal: number | null;
  packagePrice: number | null;
  savingAmount: number | null;
  savingPercent: number | null;
  fromPrice: number | null;
  discountPercent: number | null;
  /** ISO yyyy-MM-dd; shows the "Ends" pill. */
  endsAt?: string | null;
  items: PackagePhoneItem[];
}

/**
 * Spec 081b — phone-shaped preview of the app's package card (garage profile "Packages & offers" row)
 * and the package page. Text is rendered from data over the cover, never baked in; prices are a preview,
 * the server is authoritative. Styles: styles/packages.css (`.pkp`).
 */
export default function PackagePhonePreview(props: PackagePhonePreviewProps) {
  const { lang, theme, garageName, nameEn, nameAr, descriptionEn, descriptionAr, coverUrl, mode, originalTotal,
    packagePrice, savingAmount, savingPercent, fromPrice, discountPercent, endsAt, items } = props;
  const c = PHONE_COPY[lang];
  const title = (lang === 'ar' && nameAr?.trim()) ? nameAr : (nameEn.trim() || '—');
  const description = (lang === 'ar' && descriptionAr?.trim()) ? descriptionAr : (descriptionEn?.trim() || null);

  return (
    <div className={`pkp${theme === 'dark' ? ' pkp--dark' : ''}`} dir={lang === 'ar' ? 'rtl' : 'ltr'} lang={lang}
      role="figure" aria-label={c.phoneLabel} data-testid="pkg-phone-preview">
      <div className="pkp__screen">
        <div className="pkp__island" />
        <div className="pkp__status" />
        <div className="pkp__home">
          <div className="pkp__row-title">{c.row}</div>
          <div className="pkp__card" data-testid="pkg-preview-card">
            {coverUrl && <img src={coverUrl} alt="" />}
            <div className="pkp__scrim" />
            <div className="pkp__copy">
              <div className="pkp__pills">
                <span className="pkp__tag">{c.tag} · {c.services(items.length)}</span>
                {mode === 'PERCENT' && discountPercent != null && <span className="pkp__pill" data-testid="pkg-preview-off">{c.off(discountPercent)}</span>}
                {mode === 'FIXED' && savingPercent != null && savingPercent > 0 && <span className="pkp__pill">{c.off(savingPercent)}</span>}
              </div>
              <div>
                <div className="pkp__title">{title}</div>
                <div className="pkp__sub pkp__ltr" data-testid="pkg-preview-price">
                  {mode === 'FIXED'
                    ? <><s>{phoneAed(originalTotal)}</s> <b>{phoneAed(packagePrice)}</b></>
                    : <>{c.from} <b>{phoneAed(fromPrice)}</b></>}
                </div>
              </div>
              <div className="pkp__who">
                <span>{garageName}</span>
                {endsAt && <span className="pkp__ends">{c.ends(phoneShortDate(endsAt, lang))}</span>}
              </div>
            </div>
          </div>
        </div>
        <PackagePhoneSheet c={c} lang={lang} title={title} description={description} mode={mode} originalTotal={originalTotal}
          packagePrice={packagePrice} savingAmount={savingAmount} fromPrice={fromPrice} discountPercent={discountPercent} items={items} />
      </div>
    </div>
  );
}
